<template>
    <DashboardCard
        class="card"
        :icon="faSquareCheck"
        title="Focus Area"
        :show-footer="false"
        :show-error="showError"
        :on-reload-click="onReloadClick"
    >
        <template #content>
            <div v-if="loading" class="loading-container">
                <UserFocusAreaLoading />
            </div>
            <div v-else class="user-focus-area__container">
                <div
                    v-if="props.focusArea?.name"
                    class="user-focus-area__name highlight"
                >
                    "{{ props.focusArea?.name }}"
                </div>
                <div v-else>There is no Focus Area set for your team.</div>
                <div class="user-focus-area__action-items">
                    <div
                        v-for="action in props.focusArea?.actions"
                        :key="action.id"
                    >
                        <div class="user-focus-area__action-item">
                            <Checkbox
                                v-model="selectedActions"
                                :input-id="action.id"
                                :value="action.id"
                                :pt="{
                                    box: {
                                        style: {
                                            'box-sizing': 'border-box',
                                            width: '16px',
                                            height: '16px',
                                        },
                                    },
                                }"
                                class="user-focus-area__action-checkbox"
                                :disabled="!!action.acknowledged"
                                :readonly="props.displayOnly"
                                @change="() => acknowledgeAction(action)"
                            >
                            </Checkbox>

                            {{ action.action_item }}
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </DashboardCard>
</template>

<script setup lang="ts">
import { Ref, ref, watch } from 'vue'
import UserFocusAreaLoading from '@/components/FrontlineStaffDash/UserFocusAreaLoading.vue'
import DashboardCard from '@/components/FrontlineManagerDash/DashboardCard.vue'
import { faSquareCheck } from 'fontawesome/pro-regular-svg-icons'
import Checkbox from 'primevue/checkbox'
import { UserFocusArea } from '@/entities/frontlineStaffDash'

interface Props {
    onReloadClick?: Function
    focusArea?: UserFocusArea
    onActionAcknowledge: (actionId: string) => void
    loading: boolean
    showError?: boolean
    displayOnly?: boolean
}

const props = defineProps<Props>()
const selectedActions: Ref<string[]> = ref([])

const setSelectedActions = () => {
    if (props.focusArea?.actions) {
        selectedActions.value = []
        props.focusArea?.actions.forEach((action) => {
            if (action.acknowledged) {
                selectedActions.value.push(action.id)
            }
        })
    }
}

watch(
    () => props.focusArea,
    () => {
        setSelectedActions()
    },
    { immediate: true }
)

const acknowledgeAction = (action: {
    id: string
    acknowledged?: boolean
    action_item: string
}) => {
    if (props.displayOnly) {
        return
    }
    props.onActionAcknowledge(action.id)
}
</script>

<style scoped lang="less">
@import '~@/styles/prime-vue/variables.less';

.user-focus-area {
    &__container {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    &__name {
        margin-top: 12px;
        font-size: 22px;
        line-height: 26px;
        font-weight: 600;
        text-align: center;
        color: var(--p-primary-600);
    }

    &__action-items {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__action-item {
        display: flex;
        align-items: center;
        gap: 8px;
        max-height: 80px;
        font-size: 18px;
        line-height: 22px;
        font-weight: 600;
        background-color: #f4ebff;
        border-radius: @radius-xl;
        padding: 16px;
    }

    &__action-checkbox {
        align-items: center;
    }
}
</style>
